import { isFunction } from '@/utils/is';
import { PolygonSeedsFillAnimation } from './polygonSeedsFillAnimation';
import { clamp } from '@/utils/math';
import { PathManager } from './path';

export class SceneManager {
  constructor(canvasId, designSize) {
    /** @type {HTMLCanvasElement} */
    this.canvas = document.getElementById(canvasId);
    /** @type {CanvasRenderingContext2D} */
    this.ctx = this.canvas.getContext('2d');
    this.backgroundImage = new Image();
    // 设计坐标空间尺寸（所有 world 坐标均基于该空间）
    this.designSize = designSize || [1200, 600];

    this.init();
  }

  init() {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this._enableDrag = true;
    this.isNeedUpdate = false;
    this.bgSize = [0, 0];

    this.tasks = {};
    this.disposeList = [];
    this.setCanvasSize();
    this.setupResizeListeners();
  }

  get enableDrag() {
    return this._enableDrag;
  }

  set enableDrag(value) {
    if (value) {
      this?.enableDragAndZoom();
    } else {
      this?.disableDragAndZoom();
    }

    this._enableDrag = value;
  }

  setCanvasSize() {
    this.canvas.width = this.canvas.clientWidth;
    this.canvas.height = this.canvas.clientHeight;
  }

  setBackground(imageSrc) {
    this.backgroundImage.onload = () => {
      this.bgSize = [this.backgroundImage.width, this.backgroundImage.height];

      this.isInitialized = true;
      this.isNeedUpdate = true;
      this.fitBgToContainer();
    };

    this.backgroundImage.src = imageSrc;
    return this;
  }

  fitBgToContainer(animate = true) {
    const [designW, designH] = this.designSize;
    const cW = this.canvas.width;
    const cH = this.canvas.height;

    if (!designW || !designH || !cW || !cH) return this;

    const targetZoom = Math.min(cW / designW, cH / designH) * 0.95;
    const targetPanX = (cW - designW * targetZoom) / 2;
    const targetPanY = (cH - designH * targetZoom) / 2;

    if (animate) {
      this.smoothToFit(targetZoom, targetPanX, targetPanY);
    } else {
      this.zoom = targetZoom;
      this.panX = targetPanX;
      this.panY = targetPanY;
      this.isNeedUpdate = true;
    }

    return this;
  }

  // 平滑过渡到目标 zoom/pan（easeOutCubic 缓动）
  smoothToFit(targetZoom, targetPanX, targetPanY) {
    this.cancelFitAnimation();

    const startZoom = this.zoom;
    const startPanX = this.panX;
    const startPanY = this.panY;
    const duration = 300;
    const startTime = performance.now();

    const step = now => {
      const t = Math.min(1, (now - startTime) / duration);
      const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic

      this.zoom = startZoom + (targetZoom - startZoom) * ease;
      this.panX = startPanX + (targetPanX - startPanX) * ease;
      this.panY = startPanY + (targetPanY - startPanY) * ease;

      // 立即重绘，使动画平滑（不受 5fps 渲染循环限制）
      this.draw();

      if (t < 1) {
        this.fitAnimId = requestAnimationFrame(step);
      } else {
        this.fitAnimId = null;
      }
    };

    this.fitAnimId = requestAnimationFrame(step);
  }

  cancelFitAnimation() {
    if (this.fitAnimId) {
      cancelAnimationFrame(this.fitAnimId);
      this.fitAnimId = null;
    }
  }

  setCursor(cursor) {
    this.canvas.style.cursor = cursor;
  }

  drawBackground() {
    // 将底图绘制到设计坐标空间内，并保持底图原始宽高比（contain），
    // 避免因底图实际分辨率宽高比与设计尺寸不一致而导致拉伸。
    // 这样 marker / 多边形等 world 坐标（基于设计尺寸）仍与底图对齐。
    const [designW, designH] = this.designSize;
    const [imgW, imgH] = this.bgSize;

    if (!imgW || !imgH) return this;

    const scale = Math.min(designW / imgW, designH / imgH);
    const drawW = imgW * scale;
    const drawH = imgH * scale;
    const drawX = (designW - drawW) / 2;
    const drawY = (designH - drawH) / 2;

    this.ctx.drawImage(this.backgroundImage, drawX, drawY, drawW, drawH);

    return this;
  }

  setBgToCenter() {
    this.panX = (this.canvas.width - this.bgSize[0]) / 2;
    this.panY = (this.canvas.height - this.bgSize[1]) / 2;
  }

  setupDragAndZoomControls() {
    let isDragging = false;
    let lastMouseX = 0;
    let lastMouseY = 0;

    const mousedonw = e => {
      if (e.button === 0) {
        // Left mouse button
        this.cancelFitAnimation();

        isDragging = true;
        lastMouseX = e.clientX;
        lastMouseY = e.clientY;

        this.setCursor('grabbing');
      }
    };

    const mousemove = e => {
      if (isDragging) {
        const deltaX = e.clientX - lastMouseX;
        const deltaY = e.clientY - lastMouseY;

        this.panX += deltaX;
        this.panY += deltaY;

        lastMouseX = e.clientX;
        lastMouseY = e.clientY;

        this.isNeedUpdate = true;
      }
    };

    const mouseup = e => {
      if (e.button === 0) {
        isDragging = false;

        this.setCursor('grab');
      }
    };

    const mouseleave = () => {
      isDragging = false;
      this.setCursor('default');
    };

    const wheel = e => {
      e.preventDefault();

      // 用户手动缩放时，取消自动过渡动画
      this.cancelFitAnimation();

      const rect = this.canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = this.zoom * zoomFactor;

      // Limit zoom range
      if (newZoom >= 0.1 && newZoom <= 5) {
        // Zoom towards mouse position
        this.panX = mouseX - (mouseX - this.panX) * zoomFactor;
        this.panY = mouseY - (mouseY - this.panY) * zoomFactor;
        this.zoom = newZoom;

        this.isNeedUpdate = true;
      }
    };

    this.disableDragAndZoom = function () {
      this.canvas.removeEventListener('mousedown', mousedonw);
      this.canvas.removeEventListener('mousemove', mousemove);
      this.canvas.removeEventListener('mouseup', mouseup);
      this.canvas.removeEventListener('mouseleave', mouseleave);
      this.canvas.removeEventListener('wheel', wheel);
    };

    this.enableDragAndZoom = function () {
      this.disableDragAndZoom();

      this.canvas.addEventListener('mousedown', mousedonw);
      this.canvas.addEventListener('mousemove', mousemove);
      this.canvas.addEventListener('mouseup', mouseup);
      this.canvas.addEventListener('mouseleave', mouseleave);
      this.canvas.addEventListener('wheel', wheel);
    };

    this.enableDragAndZoom();

    return this;
  }

  setupResizeListeners() {
    // 使用 ResizeObserver 监听画布容器尺寸变化，
    // 而非仅监听 window resize。这样侧栏折叠、响应式布局、
    // 浏览器缩放等引起的容器尺寸变化都能及时更新画布与坐标。
    const handleResize = () => {
      const oldWidth = this.canvas.width;
      const oldHeight = this.canvas.height;

      this.setCanvasSize();

      // 尺寸未变化则无需处理
      if (oldWidth === this.canvas.width && oldHeight === this.canvas.height) return;

      // 底图已加载时，重新按容器尺寸铺满；否则仅触发重绘
      if (this.isInitialized) {
        this.fitBgToContainer();
      } else {
        this.isNeedUpdate = true;
      }
    };

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      this.resizeObserver.observe(this.canvas.parentElement || this.canvas);
    } else {
      // 兜底：不支持 ResizeObserver 时退化为 window resize 监听
      window.addEventListener('resize', handleResize);
      this.windowResizeHandler = handleResize;
    }
  }

  getScreenCoordinates(worldX, worldY) {
    return {
      x: worldX * this.zoom + this.panX,
      y: worldY * this.zoom + this.panY
    };
  }

  getWorldCoordinates(x, y) {
    const rect = this.canvas.getBoundingClientRect();

    const mouseX = x - rect.left;
    const mouseY = y - rect.top;

    return {
      x: (mouseX - this.panX) / this.zoom,
      y: (mouseY - this.panY) / this.zoom
    };
  }

  setupShapeCreation() {
    let isCreatingPolygon = false;
    let currentPoints = [];
    let tempPolygonId = null;
    let isDraggingPoint = false;
    let draggingPointIndex = -1;
    let draggingPolygonId = null;

    const finishing = () => {
      if (!isCreatingPolygon || currentPoints.length < 3) return;

      // Finalize the polygon
      if (this.shapes && tempPolygonId) {
        const tempShape = this.shapes.find(shape => shape.id === tempPolygonId);
        if (tempShape) {
          tempShape.isTemporary = false;
          delete tempShape.id;
        }
      }

      console.log('Polygon completed with', currentPoints.length, 'points');
      console.log(
        'Points:',
        currentPoints.map(p => `{ x: ${Math.round(p.x)}, y: ${Math.round(p.y)} }`)
      );

      // Reset creation state
      isCreatingPolygon = false;
      currentPoints = [];
      tempPolygonId = null;
      this.setCursor('grab');
      this.enableDrag = true;
      this.isNeedUpdate = true;
    };

    const findNearestPoint = (worldX, worldY) => {
      if (!this.shapes) return null;

      const threshold = 10 / this.zoom; // Adjust threshold based on zoom level

      for (let shapeIndex = 0; shapeIndex < this.shapes.length; shapeIndex++) {
        const shape = this.shapes[shapeIndex];
        if (shape.type === 'polygon' && shape.points) {
          for (let pointIndex = 0; pointIndex < shape.points.length; pointIndex++) {
            const point = shape.points[pointIndex];
            const distance = Math.sqrt(
              Math.pow(worldX - point.x, 2) + Math.pow(worldY - point.y, 2)
            );

            if (distance <= threshold) {
              return {
                shapeIndex,
                pointIndex,
                shape,
                point
              };
            }
          }
        }
      }
      return null;
    };

    document.addEventListener('keydown', e => {
      if (e.key === 'p' || e.key === 'P') {
        if (!isCreatingPolygon) {
          this.enableDrag = false;
          isCreatingPolygon = true;
          currentPoints = [];
          console.log(
            'Polygon creation mode activated. Click to add points, double-click to finish.'
          );
          this.setCursor('crosshair');
        } else {
          this.enableDrag = true;
          isCreatingPolygon = false;
          if (tempPolygonId !== null && this.shapes) {
            this.shapes = this.shapes.filter(shape => shape.id !== tempPolygonId);
            tempPolygonId = null;
          }
          currentPoints = [];
          console.log('Polygon creation mode deactivated.');
          this.setCursor('grab');
          this.isNeedUpdate = true;
        }
      }

      if (isCreatingPolygon && e.key === 'Enter') {
        finishing(e);
      }
    });

    // Add mouse events for point dragging
    this.canvas.addEventListener('mousedown', e => {
      if (isCreatingPolygon || !this.isInitialized) return;

      const worldCoords = this.getWorldCoordinates(e.clientX, e.clientY);
      const nearestPoint = findNearestPoint(worldCoords.x, worldCoords.y);

      if (nearestPoint && e.button === 0) {
        isDraggingPoint = true;
        draggingPointIndex = nearestPoint.pointIndex;
        draggingPolygonId = nearestPoint.shapeIndex;
        this.enableDrag = false;
        this.setCursor('move');
        e.preventDefault();
      }
    });

    this.canvas.addEventListener('mousemove', e => {
      if (!this.isInitialized) return;

      const worldCoords = this.getWorldCoordinates(e.clientX, e.clientY);

      if (isDraggingPoint && draggingPolygonId !== null && draggingPointIndex !== -1) {
        // Update the point position
        const shape = this.shapes[draggingPolygonId];
        if (shape && shape.points && shape.points[draggingPointIndex]) {
          shape.points[draggingPointIndex].x = worldCoords.x;
          shape.points[draggingPointIndex].y = worldCoords.y;
          this.isNeedUpdate = true;
        }
      } else if (!isCreatingPolygon) {
        // Check if hovering over a point and change cursor
        const nearestPoint = findNearestPoint(worldCoords.x, worldCoords.y);
        if (nearestPoint) {
          this.setCursor('move');
        } else {
          this.setCursor('grab');
        }
      }
    });

    this.canvas.addEventListener('mouseup', () => {
      if (isDraggingPoint) {
        isDraggingPoint = false;
        draggingPointIndex = -1;
        draggingPolygonId = null;
        this.enableDrag = true;
        this.setCursor('grab');

        console.log(this.shapes);
      }
    });

    this.canvas.addEventListener('mouseleave', () => {
      if (isDraggingPoint) {
        isDraggingPoint = false;
        draggingPointIndex = -1;
        draggingPolygonId = null;
        this.enableDrag = true;
        this.setCursor('default');
      }
    });

    // Add click listener for adding points
    this.canvas.addEventListener('click', e => {
      if (!isCreatingPolygon || !this.isInitialized) return;

      const worldCoords = this.getWorldCoordinates(e.clientX, e.clientY);

      currentPoints.push({ x: worldCoords.x, y: worldCoords.y });
      console.log(`Point added: (${Math.round(worldCoords.x)}, ${Math.round(worldCoords.y)})`);

      // If this is the first point, create a temporary polygon
      if (currentPoints.length === 1) {
        tempPolygonId = 'temp_' + Date.now();
        this.addShape({
          id: tempPolygonId,
          type: 'polygon',
          points: [...currentPoints],
          isTemporary: true
        });
      } else {
        // Update the temporary polygon
        if (this.shapes && tempPolygonId) {
          const tempShape = this.shapes.find(shape => shape.id === tempPolygonId);
          if (tempShape) {
            tempShape.points = [...currentPoints];
          }
        }
      }

      this.isNeedUpdate = true;
    });

    // Add double-click listener for finishing polygon
    this.canvas.addEventListener('dblclick', finishing);
    return this;
  }

  addCssMarker(x, y, options = {}) {
    const { className = 'css-marker', createEl, zIndex = 1000 } = options;

    // Create marker element
    const markerElement = document.createElement('div');
    markerElement.className = className;
    markerElement.style.cssText = `
          position: absolute;
          transform: translate(-50%, -50%);
          z-index: ${zIndex};
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        `;

    if (isFunction(createEl)) {
      markerElement.appendChild(createEl());
    }

    // Create marker object
    const cssMarker = {
      x: x,
      y: y,
      element: markerElement,
      id: 'css_marker_' + Date.now() + '_' + Math.random(),
      updatePosition: () => {
        const screenCoords = this.getScreenCoordinates(cssMarker.x, cssMarker.y);

        markerElement.style.scale = clamp(this.zoom, 0.4, 1);
        markerElement.style.left = screenCoords.x + 'px';
        markerElement.style.top = screenCoords.y + 'px';
        markerElement.style.zIndex = parseInt(screenCoords.y);
      },
      remove: () => {
        if (markerElement.parentNode) {
          markerElement.parentNode.removeChild(markerElement);
        }
        if (this.cssMarkers) {
          const index = this.cssMarkers.indexOf(cssMarker);
          if (index > -1) {
            this.cssMarkers.splice(index, 1);
          }
        }
      }
    };

    // Initialize CSS markers array if not exists
    if (!this.cssMarkers) {
      this.cssMarkers = [];
    }

    // Add to CSS markers array
    this.cssMarkers.push(cssMarker);

    // Add to DOM
    this.canvas.parentElement.appendChild(markerElement);

    // Set initial position
    cssMarker.updatePosition();

    return cssMarker;
  }

  updateCssMarkers() {
    if (this.cssMarkers) {
      this.cssMarkers.forEach(marker => {
        marker.updatePosition();
      });
    }
  }

  clearCssMarkers() {
    if (this.cssMarkers) {
      this.cssMarkers.forEach(marker => {
        marker.remove();
      });
      this.cssMarkers = [];
    }
    return this;
  }

  addPolygon(points) {
    if (!points || points.length < 3) {
      console.warn('A polygon needs at least 3 points');
      return this;
    }

    return this.addShape({
      type: 'polygon',
      points: points
    });
  }

  drawShape(shapes) {
    shapes.forEach(shape => {
      const { strokeColor, fillColor, lineWidth } = shape?.property || {
        strokeColor: 'yellow',
        fillColor: 'transparnet',
        lineWidth: 2
      };

      this.ctx.strokeStyle = strokeColor;
      this.ctx.lineWidth = lineWidth;
      this.ctx.beginPath();

      this.ctx.fillStyle = fillColor;

      if (shape.type === 'rectangle') {
        // Draw rectangular shape range
        this.ctx.rect(shape.x, shape.y, shape.width, shape.height);
      } else if (shape.type === 'circle') {
        // Draw circular shape range
        this.ctx.arc(shape.x, shape.y, shape.radius, 0, 2 * Math.PI);
      } else if (shape.type === 'polygon' && shape.points && shape.points.length >= 3) {
        // Draw custom polygon shape range
        this.ctx.moveTo(shape.points[0].x, shape.points[0].y);
        for (let i = 1; i < shape.points.length; i++) {
          this.ctx.lineTo(shape.points[i].x, shape.points[i].y);
        }
        this.ctx.closePath();
      }

      // Fill and stroke the shape range
      this.ctx.fill();
      this.ctx.stroke();

      // Draw shape name if property exists
      if (shape.property && shape.property.name) {
        // Calculate polygon center
        let centerX = 0;
        let centerY = 0;
        if (shape.points && shape.points.length > 0) {
          shape.points.forEach(point => {
            centerX += point.x;
            centerY += point.y;
          });
          centerX /= shape.points.length;
          centerY /= shape.points.length;
        } else if (shape.type === 'rectangle') {
          centerX = shape.x + shape.width / 2;
          centerY = shape.y + shape.height / 2;
        } else if (shape.type === 'circle') {
          centerX = shape.x;
          centerY = shape.y;
        }

        const { name, offset = [0, 0] } = shape.property;

        this.addText(name, centerX + offset[0], centerY + offset[1]);
      }
    });
  }

  addText(text, centerX, centerY) {
    // Set text properties
    this.ctx.fillStyle = 'black';
    this.ctx.font = `${14}px Arial`;
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';

    // Draw text with background
    const textWidth = this.ctx.measureText(text).width;
    const textHeight = 14;
    const padding = 4;

    // Draw background rectangle
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    this.ctx.fillRect(
      centerX - textWidth / 2 - padding,
      centerY - textHeight / 2 - padding,
      textWidth + padding * 2,
      textHeight + padding * 2
    );

    // Draw text
    this.ctx.fillStyle = 'black';
    this.ctx.fillText(text, centerX, centerY);
  }

  addShape(shapeConfig) {
    if (!this.shapes) {
      this.shapes = [];
    }
    this.shapes.push(shapeConfig);

    return this;
  }

  addShapes(shapeConfigs) {
    shapeConfigs.forEach(shapeConfig => this.addShape(shapeConfig));

    return this;
  }

  clearShapes() {
    this.shapes = [];
    this.draw();
  }

  startRenderLoop(fps) {
    const renderLoop = () => {
      const interval = 1000 / fps;
      let lastTime = 0;

      const currentTime = Date.now();

      if (currentTime - lastTime >= interval) {
        lastTime = currentTime;

        if (this.isNeedUpdate) {
          this.draw();
        }
      }

      requestAnimationFrame(renderLoop);
    };

    renderLoop();
  }

  setupMarkerControls() {
    let isAddingMarker = false;
    let markers = [];
    let isDraggingMarker = false;
    let draggingMarkerIndex = -1;

    const addMarker = (worldX, worldY, label = '') => {
      const marker = {
        x: worldX,
        y: worldY,
        label: label || `Marker ${markers.length + 1}`,
        id: 'marker_' + Date.now() + '_' + Math.random()
      };
      markers.push(marker);
      this.isNeedUpdate = true;
      return marker;
    };

    const findNearestMarker = (worldX, worldY) => {
      const threshold = 15 / this.zoom;

      for (let i = 0; i < markers.length; i++) {
        const marker = markers[i];
        const distance = Math.sqrt(Math.pow(worldX - marker.x, 2) + Math.pow(worldY - marker.y, 2));

        if (distance <= threshold) {
          return { index: i, marker };
        }
      }
      return null;
    };

    const deleteMarker = index => {
      if (index >= 0 && index < markers.length) {
        markers.splice(index, 1);
        this.isNeedUpdate = true;
      }
    };

    // Keyboard controls
    document.addEventListener('keydown', e => {
      if (e.key === 'm' || e.key === 'M') {
        if (!isAddingMarker) {
          this.enableDrag = false;
          isAddingMarker = true;
          console.log('Marker mode activated. Click to add markers, press M again to exit.');
          this.setCursor('crosshair');
        } else {
          this.enableDrag = true;
          isAddingMarker = false;

          console.log(this.markers);

          console.log('Marker mode deactivated.');
          this.setCursor('grab');
        }
      }
    });

    // Mouse controls for markers
    this.canvas.addEventListener('mousedown', e => {
      if (isAddingMarker || !this.isInitialized) return;

      const worldCoords = this.getWorldCoordinates(e.clientX, e.clientY);
      const nearestMarker = findNearestMarker(worldCoords.x, worldCoords.y);

      if (nearestMarker && e.button === 0) {
        isDraggingMarker = true;
        draggingMarkerIndex = nearestMarker.index;
        this.enableDrag = false;
        this.setCursor('move');
        e.preventDefault();
      }
    });

    this.canvas.addEventListener('mousemove', e => {
      if (!this.isInitialized) return;

      const worldCoords = this.getWorldCoordinates(e.clientX, e.clientY);

      if (isDraggingMarker && draggingMarkerIndex !== -1) {
        const marker = markers[draggingMarkerIndex];
        if (marker) {
          marker.x = worldCoords.x;
          marker.y = worldCoords.y;
          this.isNeedUpdate = true;
        }
      } else if (!isAddingMarker) {
        const nearestMarker = findNearestMarker(worldCoords.x, worldCoords.y);
        if (nearestMarker) {
          this.setCursor('move');
        } else {
          this.setCursor('grab');
        }
      }
    });

    this.canvas.addEventListener('mouseup', () => {
      if (isDraggingMarker) {
        isDraggingMarker = false;
        draggingMarkerIndex = -1;
        this.enableDrag = true;
        this.setCursor('grab');
      }
    });

    this.canvas.addEventListener('mouseleave', () => {
      if (isDraggingMarker) {
        isDraggingMarker = false;
        draggingMarkerIndex = -1;
        this.enableDrag = true;
        this.setCursor('default');
      }
    });

    // Click to add markers
    this.canvas.addEventListener('click', e => {
      if (!isAddingMarker || !this.isInitialized) return;

      const worldCoords = this.getWorldCoordinates(e.clientX, e.clientY);
      const label = prompt('Enter marker label:', `Marker ${markers.length + 1}`);

      if (label !== null) {
        addMarker(worldCoords.x, worldCoords.y, label);
        console.log(
          `Marker added at (${Math.round(worldCoords.x)}, ${Math.round(
            worldCoords.y
          )}) with label: ${label}`
        );
      }
    });

    // Right click to delete markers
    this.canvas.addEventListener('contextmenu', e => {
      e.preventDefault();

      if (isAddingMarker || !this.isInitialized) return;

      const worldCoords = this.getWorldCoordinates(e.clientX, e.clientY);
      const nearestMarker = findNearestMarker(worldCoords.x, worldCoords.y);

      if (nearestMarker) {
        if (confirm(`Delete marker "${nearestMarker.marker.label}"?`)) {
          deleteMarker(nearestMarker.index);
          console.log(`Marker "${nearestMarker.marker.label}" deleted.`);
        }
      }
    });

    // Store markers in scene manager
    this.markers = markers;

    return this;
  }

  drawMarkers() {
    if (!this.markers || this.markers.length === 0) return;

    this.markers.forEach(marker => {
      // Draw marker circle
      this.ctx.fillStyle = 'rgba(255, 165, 0, 0.8)';
      this.ctx.strokeStyle = 'orange';
      this.ctx.lineWidth = 2;

      this.ctx.beginPath();
      this.ctx.arc(marker.x, marker.y, 8, 0, 2 * Math.PI);
      this.ctx.fill();
      this.ctx.stroke();

      // Draw marker center dot
      this.ctx.fillStyle = 'white';
      this.ctx.beginPath();
      this.ctx.arc(marker.x, marker.y, 2, 0, 2 * Math.PI);
      this.ctx.fill();

      // Draw label
      if (marker.label) {
        this.addText(marker.label, marker.x, marker.y - 25);
      }
    });
  }

  addMarker(x, y, label) {
    if (!this.markers) {
      this.markers = [];
    }

    const marker = {
      x: x,
      y: y,
      label: label || `Marker ${this.markers.length + 1}`,
      id: 'marker_' + Date.now() + '_' + Math.random()
    };

    this.markers.push(marker);
    this.isNeedUpdate = true;

    return this;
  }

  clearMarkers() {
    this.markers = [];
    this.isNeedUpdate = true;
    return this;
  }

  usePolygonSeedsFillAnimation({ seeds, speed, polygon }) {
    const polygonSeedsFillAnimation = new PolygonSeedsFillAnimation({
      scene: this,
      polygon,
      seeds,
      speed
    });

    return polygonSeedsFillAnimation;
  }

  usePathManager() {
    const pathManager = new PathManager({ canvas: this.canvas, ctx: this.ctx });

    return pathManager;
  }

  setupPointerPosControl() {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.left = '0';
    div.style.color = 'white';
    div.style.zIndex = '9999';
    div.style.fontSize = '20px';
    div.style.fontFamily = 'Arial';
    div.style.background = 'rgba(0,80,100, 0.8)';
    div.style.padding = '10px';

    const container = this.canvas.parentElement;

    container.appendChild(div);

    div.innerText = `x: 0 y: 0`;

    const mousemove = e => {
      const { x, y } = this.getWorldCoordinates(e.clientX, e.clientY);
      div.innerText = `x: ${x.toFixed(2)} y: ${y.toFixed(2)}`;
    };

    this.canvas.addEventListener('mousemove', mousemove);

    this.disposeList.push(() => {
      container.removeChild(div);
      this.canvas.removeEventListener('mousemove', mousemove);
    });

    return this;
  }

  addTask(taskName, task) {
    this.tasks[taskName] = task;
  }

  removeTask(taskName) {
    delete this.tasks[taskName];
  }

  executeTasks() {
    Object.values(this.tasks).forEach(task => {
      task(this.time);
    });
  }

  draw() {
    this.isNeedUpdate = false;

    this.ctx.save();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Then apply transformations and draw shapes on top
    this.ctx.translate(this.panX, this.panY);
    this.ctx.scale(this.zoom, this.zoom);

    // Draw background first
    if (this.backgroundImage.complete) {
      this.drawBackground();
    }

    // Draw shapes after background
    if (this.shapes && this.shapes.length > 0) {
      this.drawShape(this.shapes);
    }

    if (this.markers?.length > 0) {
      this.drawMarkers();
    }

    this.executeTasks();

    this.ctx.restore();

    // Update CSS markers after drawing
    this.updateCssMarkers();
  }

  dispose() {
    // 清理平滑过渡动画
    this.cancelFitAnimation();

    // 清理容器尺寸监听
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
      this.windowResizeHandler = null;
    }

    this.disposeList.forEach(e => {
      e?.();
    });
  }
}
